<template>
  <BaseCardV2
    :chip="hasChip ? {libelle: 'Prix ferme'} : null"
    :erreur="erreur"
    :has-btn-action="false"
    :loading="loading"
    :titre="titre"
    class="pa-2"
    has-help
    @on-help="isOpenModalHelp = true"
  >
    <div>
      <div
        v-if="!loading"
      >
        <div
          v-if="hasSelect"
          class="mt-2 ml-2"
        >
          <BaseSelect
            :items="typesContrat"
            :libelle-btn="typeContratSelected.libelle"
            @onClick="onSelectTypePrix($event)"
          />
        </div>
        <div
          v-if="valeurPerso"
          class="d-flex justify-center"
        >
          <span
            :class="$vuetify.breakpoint.mobile ? 'chiffre' : 'chiffre-xxl '"
            class=" align-self-center secondary--text"
          >
            {{ valeurPerso | roundNumber(2) }}
          </span>
          <span
            :class="$vuetify.breakpoint.mobile ? 'texte' : 'chiffre'"
            class="ml-2 align-self-center secondary--text"
          >€/t</span>
        </div>
        <div
          v-if="repartitionPrix.evolutionValeurDuNumtiers"
          class="mt-2 d-flex justify-center"
        >
          <BaseEvolution
            :evolution-value="repartitionPrix.evolutionValeurDuNumtiers"
            :unite="'€'"
          />
        </div>
        <div
          v-else-if="evolutionAdherent!=null && evolutionAdherent.evolutionPrixVenteMoyenEnPourcentage!= null"
          class="mt-2 d-flex justify-center"
        >
          <BaseEvolutionWithInfo
            :evolution-value="evolutionAdherent.evolutionPrixVenteMoyenEnPourcentage"
            :values="evolutionAdherent.prixVenteMoyenParAnnee"
            :unite="'€'"
          />
        </div>
        <v-divider
          v-if="hasRegionSelected"
          class="mx-4 mt-6"
        />
        <div
          v-if="!isDonneesGroupeInsuffisante(repartitionPrix) && hasRegionSelected && !loading"
          class=" mx-5 my-5 texte texte-gris-fonce"
        >
          Où se situe ma donnée par rapport à mon groupe ({{ repartitionPrix.nbAdherentsGroupe }} adhérents) ?
        </div>
        <div
          v-if="(isDonneesGroupeInsuffisante(repartitionPrix) && !loading) || !hasRegionSelected"
          class=" mx-5 mt-5 texte texte-gris-fonce"
        >
          <BaseMessageV2 message="Nombre d'adhérents insuffisant pour la comparaison (&lt; 5 adhérent)" />
        </div>
        <!-- graphe prix moyens -->
        <ComparaisonQuartiles
          v-if="!loading && !isDonneesGroupeInsuffisante(repartitionPrix)"
          class="d-flex justify-center"
          :data="chartData"
          size="100%"
        />
      </div>
      <HelpModal
        v-if="isOpenModalHelp"
        :titre="affichageAidePrixContrat ? 'Mon prix de contrat moyen' : 'Mon prix payé moyen'"
        :is-open-modal-help="isOpenModalHelp"
        @close="isOpenModalHelp = false"
      >
        <div slot="baseCardHelpContent">
          <div
            v-if="affichageAidePrixContrat"
            class="texte text-justify texte-gris-fonce pa-8"
          >
            <p>Comparez les prix de base de vos contrats sur ce graphique. Les prix comparés correspondent à la moyenne des prix ferme par adhérent.</p>
            <p>Si vous souhaitez visualiser l'ensemble des primes ou réfactions appliquées, veuillez vous rendre sur le graphique de répartition des prix payés.</p>
          </div>
          <div
            v-else-if="affichageAidePrixMoyen"
            class="texte text-justify texte-gris-fonce pa-8"
          >
            <p>Le prix moyen affiché est la moyenne des prix payés lorsqu'ils sont disponibles. En l'absence du prix payé, c'est le prix de contrat qui est considéré. Les prix de contrats ou les prix payés peuvent être provisoires (ex : acompte prix de campagne). Cette moyenne sera donc amenée à évoluer tout au long de la campagne de commercialisation.</p>
            <p>Lorsqu'il n'y a pas de prix disponible (ex : contrat échange céréales, prix de campagne en amont de l'acompte), le contrat n'est alors pas pris en compte dans le calcul de la moyenne de prix.</p>
            <p>L'acompte du tréso moisson n'est pas comptabilisé puisque le contrat final n'est pas encore fixé.</p>
            <li class="ml-4">
              Prix ferme = Prix ferme avec option + prix ferme sans option
            </li>
            <li class="ml-4">
              Gestion déléguée = ensemble des contrats en gestion déléguée
            </li>
            <li class="ml-4">
              Autres = contrats filières + contrats autres + contrats échanges + ventes sur pied"
            </li>
          </div>
          <div
            v-else
            class="texte text-justify texte-gris-fonce pa-8"
          >
            <p>Vous pouvez comparer pour la graine sélectionnée votre prix payé moyen de l'année par rapport aux autres adhérents de votre groupe.</p>
            <p>Celui-ci comprend les bonifications/réfactions qualités, la prime logistique, les frais de stockage et la cotation prix ferme.</p>
            <p>Les prix payés moyen des adhérents de votre filtre de comparaison sont triés du plus petit au plus grand et réparties en 4 parts égales, de sorte que chaque partie représente précisément ¼ des producteurs ayant complété leurs données.</p>
            <p>Les bornes (quartiles) sont indiquées :</p>
            <li>Min : valeur minimum des producteurs considérés.</li>
            <li>Quart inférieur : 25% des producteurs considérés ont une valeur inférieure à ce seuil.</li>
            <li>Médiane: partage les producteurs considérés en 2 parties égales: 50% des producteurs ont une valeur inférieure et 50% ont une valeur supérieure.</li>
            <li>Quart supérieur : 75% des producteurs considérés ont une valeur inférieure à ce seuil.</li>
            <li>Max : valeur maximum des producteurs considérés.</li>
            <p>Le curseur représente votre propre valeur et il est positionné sur cette jauge en fonction de votre situation.</p>
          </div>
        </div>
      </HelpModal>
    </div>
  </BaseCardV2>
</template>

<script>
  import { mapGetters } from 'vuex'
import numberFilter from '@/filters/number.filter'
import BaseCardV2 from '@/components/card/base-card-v2.vue'
import BaseEvolution from '@/components/common/base-evolution.vue'
import BaseMessageV2 from '@/components/common/base-message-v2.vue'
import BaseSelect from '@/components/common/base-select.vue'
import ComparaisonQuartiles from '@/components/chart/v2/comparaison-quartiles.vue'
import HelpModal from '@/components/modal/help-modal.vue'
import BaseEvolutionWithInfo from '@/components/common/base-evolution-with-info.vue'

export default {
  name: 'JaugePrixMoyens',
  components: { BaseCardV2, BaseEvolution, BaseEvolutionWithInfo, BaseMessageV2, BaseSelect, ComparaisonQuartiles, HelpModal  },
  props: {
    repartitionPrix: {
      type: Object,
      required: true
    },
    evolutionAdherent: {
      type: Object,
      default: null
    },
    titre: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    erreur: {
      type: Boolean,
      default: false
    },
    affichageAidePrixContrat: {
      type: Boolean,
      default: false
    },
    affichageAidePrixMoyen: {
      type: Boolean,
      default: false
    },
    baseCardClass: {
      type: String,
      default: ''
    },
    hasRegionSelected: {
      type: Boolean,
      default: false
    },
    hasChip: {
      type: Boolean,
      default: true
    },
    hasSelect: {
      type: Boolean,
      default: false
    },
    typeContratSelected: {
      type: Object,
      default: null
    },
  },
  data () {
    return {
      isOpenModalHelp: false,
      typeContratModifiable: null
    }
  },

  computed: {
    ...mapGetters({
      typesContrat: 'contratVente/filtreTypesContrats'
    }),
    valeurPerso () {
      return this.repartitionPrix.positionPersoDuNumtiers && this.repartitionPrix.positionPersoDuNumtiers.valeur ? this.repartitionPrix.positionPersoDuNumtiers.valeur : null
    },
    chartData () {
      return {
        measure: {
          value: this.repartitionPrix.positionPersoDuNumtiers ? numberFilter.formatNumber(this.repartitionPrix.positionPersoDuNumtiers.valeur, 0) : null,
          legend: this.repartitionPrix.positionPersoDuNumtiers ? numberFilter.formatNumber(this.repartitionPrix.positionPersoDuNumtiers.valeur,2) + ' €/t' : null
        },
        min: {
          value: this.repartitionPrix.min ? numberFilter.formatNumber(this.repartitionPrix.min.valeur, 0) : null,
          label: 'Min'
        },
        quartile1: {
          value: this.repartitionPrix.quartile1 ? numberFilter.formatNumber(this.repartitionPrix.quartile1.valeur, 0) : null,
          label: 'Quart inf.'
        },
        mediane: {
          value: this.repartitionPrix.quartile2 ? numberFilter.formatNumber(this.repartitionPrix.quartile2.valeur, 0) : null,
          label: 'Médiane'
        },
        quartile2: {
          value: this.repartitionPrix.quartile3 ? numberFilter.formatNumber(this.repartitionPrix.quartile3.valeur, 0) : null,
          label: 'Quart sup.'
        },
        max: {
          value: this.repartitionPrix.max ? numberFilter.formatNumber(this.repartitionPrix.max.valeur, 0) : null,
          label: 'Max'
        },
  
        quarters: [
          { color: '#ec6608', legend: this.repartitionPrix.quartile1 ? this.repartitionPrix.quartile1.nombreValeursInferieures + ' producteur(s) ont un prix compris entre ' + numberFilter.formatNumber(this.repartitionPrix.min.valeur,0) + ' € et ' + numberFilter.formatNumber(this.repartitionPrix.quartile1.valeur,0) + ' €' : null},
          { legend: this.repartitionPrix.quartile2 ? (this.repartitionPrix.quartile2.nombreValeursInferieures - this.repartitionPrix.quartile1.nombreValeursInferieures) + ' producteur(s) ont un prix compris entre ' + numberFilter.formatNumber(this.repartitionPrix.quartile1.valeur,0) + ' € et ' + numberFilter.formatNumber(this.repartitionPrix.quartile2.valeur,0) + ' €' : null},
          { legend: this.repartitionPrix.quartile3 ? (this.repartitionPrix.quartile3.nombreValeursInferieures - this.repartitionPrix.quartile2.nombreValeursInferieures) + ' producteur(s) ont un prix compris entre ' + numberFilter.formatNumber(this.repartitionPrix.quartile2.valeur,0) + ' € et ' + numberFilter.formatNumber(this.repartitionPrix.quartile3.valeur,0) + ' €' : null},
          { color: '#01713C', legend: this.repartitionPrix.max ? (this.repartitionPrix.max.nombreValeursInferieures - this.repartitionPrix.quartile3.nombreValeursInferieures +1) + ' producteur(s) ont un prix compris entre ' + numberFilter.formatNumber(this.repartitionPrix.quartile3.valeur,0) + ' € et ' + numberFilter.formatNumber(this.repartitionPrix.max.valeur,0) + ' €' : null}
        ],
        unit: '€',
        icon: 'mdi-account'
      }
    }
  },
  created () {
    this.typeContratModifiable = this.typeContratSelected
  },

  methods: {
    isDonneesGroupeInsuffisante (repartition) {
      return repartition.nbAdherentsGroupe < 5
    },
    onSelectTypePrix (code) {
      this.typeContratModifiable = this.typesContrat.find(t => t.code === code)
      this.$emit('updateFiltreTypeContrat', this.typeContratModifiable)
    }
  }
}

</script>
